import { useState } from "preact/hooks";

interface HeaderProps {
  username?: string;
  ign?: string;
}

export default function Header({ username, ign }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header class="h-16 w-full bg-gray-900/40 backdrop-blur-md border-b border-white/10 select-none fixed top-0 z-50">
      <div class="flex items-center justify-between px-4 sm:px-8 h-16 max-w-full mx-auto relative">
        <a
          href="/"
          class="flex gap-3 items-center z-50 group"
        >
          <div class="w-10 h-10 flex-shrink-0 relative transition-transform group-hover:scale-110 duration-300">
            <img
              src="ocreIcon.png"
              alt="DofusMob Logo"
              class="object-cover w-full h-full drop-shadow-[0_0_8px_rgba(234,179,8,0.3)]"
            />
          </div>
          <h1 class="text-xl font-black text-white tracking-tighter group-hover:text-accent transition-colors">DofusMob</h1>
        </a>

        {/* Desktop Navigation */}
        <div class="hidden md:flex absolute left-1/2 transform -translate-x-1/2 justify-center items-center gap-1 bg-white/5 border border-white/10 rounded-2xl p-1 backdrop-blur-sm">
          <a
            href="/monsters"
            class="px-5 py-2 text-sm font-bold text-gray-400 hover:text-white hover:bg-white/5 rounded-xl transition-all"
          >
            Mes monstres
          </a>
          <div class="w-px h-4 bg-white/10 mx-1"></div>
          <a
            href="/market"
            class="px-5 py-2 text-sm font-bold text-gray-400 hover:text-white hover:bg-white/5 rounded-xl transition-all"
          >
            Place Marchande
          </a>
        </div>

        {/* Desktop Profile/Auth */}
        <div class="hidden md:flex items-center gap-4">
          {username && ign ? (
            <div class="flex items-center">
              <a
                href="/profile"
                class="flex items-center gap-3 px-4 py-2 bg-white/5 border border-white/10 hover:border-accent/50 hover:bg-white/10 rounded-2xl transition-all group"
              >
                <div class="text-right hidden lg:block">
                    <p class="text-xs font-bold text-gray-500 uppercase tracking-widest leading-none mb-1">Joueur</p>
                    <p class="text-sm font-black text-white group-hover:text-accent transition-colors">{ign}</p>
                </div>
                <div class="p-2 rounded-xl bg-accent/10 text-accent">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
                      <path fill-rule="evenodd" d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653Zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438ZM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" clip-rule="evenodd" />
                    </svg>
                </div>
              </a>
            </div>
          ) : (
            <div class="flex items-center gap-2">
              <a
                href="/login"
                class="px-5 py-2 text-sm font-bold text-gray-400 hover:text-white transition-all"
              >
                Sign In
              </a>
              <a
                href="/login?formType=signup"
                class="px-6 py-2 bg-accent hover:bg-yellow-500 text-gray-900 rounded-xl text-sm font-black transition-all shadow-[0_0_15px_rgba(234,179,8,0.2)]"
              >
                Sign Up
              </a>
            </div>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          class="md:hidden p-2 text-accent focus:outline-none z-50"
          aria-label="Toggle menu"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMenuOpen ? (
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Mobile Menu Overlay */}
        <div
          class={`fixed inset-0 bg-gray-950/95 backdrop-blur-2xl z-40 flex flex-col items-center justify-center transition-all duration-500 md:hidden ${
            isMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full pointer-events-none"
          }`}
        >
          {/* Background glow for mobile menu */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-accent/10 blur-[100px] -z-10 animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-600/10 blur-[150px] -z-10"></div>

          <nav class="flex flex-col items-center gap-10">
            <a
              href="/monsters"
              class="text-3xl font-black text-white hover:text-accent transition-colors tracking-tight"
              onClick={() => setIsMenuOpen(false)}
            >
              Mes monstres
            </a>
            <a
              href="/market"
              class="text-3xl font-black text-white hover:text-accent transition-colors tracking-tight"
              onClick={() => setIsMenuOpen(false)}
            >
              Place Marchande
            </a>
            
            <div class="w-12 h-1 bg-white/10 rounded-full"></div>
            
            {username && ign ? (
              <a
                href="/profile"
                class="flex flex-col items-center gap-4 group"
                onClick={() => setIsMenuOpen(false)}
              >
                <div class="p-4 rounded-3xl bg-accent/10 text-accent border border-accent/20 group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-10 h-10">
                    <path fill-rule="evenodd" d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653Zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438ZM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="text-center">
                  <p class="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Connecté en tant que</p>
                  <p class="text-2xl font-black text-white">{ign}</p>
                </div>
              </a>
            ) : (
              <div class="flex flex-col gap-6 items-center w-full px-8">
                <a
                  href="/login"
                  class="text-2xl font-bold text-gray-400 hover:text-white transition-all"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign In
                </a>
                <a
                  href="/login?formType=signup"
                  class="w-full bg-accent text-gray-900 px-8 py-4 rounded-2xl font-black text-xl text-center shadow-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </a>
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
